import { flow, getEnv, Instance, types } from "mobx-state-tree";

import { DamageInput } from "../../api-models/request/DamageInput";
import { getImageFormData } from "../form/FormAppenders";
import { DamageGroupCode } from "./DamageGroupCode";
import serializeToQueryParams from "../../helpers/QueryParams";
import { endpoints } from "../api/ApiEndpoints";
import {
  DamageMetadataResponse,
  emptyDamageMetadata,
} from "../../api-models/response/DamageMetadataResponse";
import { DamageC2CodeResponse } from "../../api-models/response/DamageMainCodeResponse";
import {
  DamageDetailsForCodesResponse,
  emptyDamageDetailsForCodes,
} from "../../api-models/response/DamageDetailsForCodesResponse";
import { DamageGroupCodeResponse } from "../../api-models/response/DamageGroupCodeResponse";

export const DamagePropertiesStorageModel = types
  .model({
    sewerObjectId: types.number,
    damageMetadata: types.optional(types.frozen<DamageMetadataResponse>(), emptyDamageMetadata),
    groupCodes: types.optional(types.array(types.frozen<DamageGroupCode>()), []),
    c2Codes: types.optional(types.array(types.frozen<DamageC2CodeResponse>()), []),
    damageDetailsForCodes: types.optional(
      types.frozen<DamageDetailsForCodesResponse>(),
      emptyDamageDetailsForCodes
    ),
  })
  .named("DamagePropertiesStorageModel")
  .actions((self) => {
    return {
      getDamageMetadata: flow(function* getDamageMetadata() {
        const endpoint = endpoints.get("damages_metadata");
        endpoint.queryParams = `sewerObjectId=${self.sewerObjectId}`;

        self.damageMetadata = yield getEnv(self).api.get(endpoint);
      }),
      getDamageGroupCodes: flow(function* getDamageGroupCodes() {
        const endpoint = endpoints.get("damages_group_codes");
        endpoint.queryParams = `sewerObjectId=${self.sewerObjectId}`;
        const response = yield getEnv(self).api.get(endpoint);

        self.groupCodes = response.map(
          (damageGroupCodeResponse: DamageGroupCodeResponse) =>
            new DamageGroupCode(damageGroupCodeResponse)
        );
      }),
      getDamageC2CodesForMainAndC1Code: flow(function* getDamageC2CodesForMainAndC1Code(
        mainCode: string,
        c1Code: string
      ) {
        const queryParams = serializeToQueryParams({
          sewerObjectId: self.sewerObjectId,
          mainCode,
          c1Code,
        });
        const endpoint = endpoints.get("damages_c2_codes");
        endpoint.queryParams = queryParams;

        self.c2Codes = yield getEnv(self).api.get(endpoint);
      }),
      getDamageDetailsForCodes: flow(function* getDamageDetailsForCodes(
        mainCode: string,
        c1Code: string,
        c2Code: string
      ) {
        const queryParams = serializeToQueryParams({
          sewerObjectId: self.sewerObjectId,
          mainCode,
          c1Code,
          c2Code,
        });
        const endpoint = endpoints.get("damages_details_for_codes");
        endpoint.queryParams = queryParams;

        self.damageDetailsForCodes = yield getEnv(self).api.get(endpoint);
      }),
      createDamage: flow(function* createDamage(inspectionId: number, damage: DamageInput) {
        const damageForm = getImageFormData(damage.file);
        damageForm.append("dynamicProperties", JSON.stringify(damage.dynamicProperties));

        const endpoint = endpoints.get("damages");
        endpoint.queryParams = `inspectionId=${inspectionId}`;

        return yield getEnv(self).api.post(endpoint, damageForm);
      }),
    };
  })
  .views((self) => ({
    get areGroupCodesLoading() {
      return !self.groupCodes.length;
    },
    get areDamagesMetadataLoading() {
      return !self.damageMetadata?.metadata.length;
    },
  }))
  .views((self) => ({
    get isDataLoadingForCreate() {
      return self.areGroupCodesLoading || self.areDamagesMetadataLoading;
    },
  }));

export type DamagePropertiesStorage = Instance<typeof DamagePropertiesStorageModel>;
